import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import { useCrud } from '../../hooks/useApi';
import FormPageShell from '../../components/Common/FormPageShell';
import PageHeader from '../../components/Common/PageHeader';
import Can from '../../components/Common/Can';
import { getAppHomePath } from '../../constants/navigation';
import { P } from '../../constants/permissions';
import ProfessionalCouncilModal from '../../components/Professional/ProfessionalCouncilModal';
import ProfessionalCouncilTable from '../../components/Professional/ProfessionalCouncilTable';
import ProfessionalCouncilFilters from '../../components/Professional/ProfessionalCouncilFilters';
import { professionalService } from '../../services/api';
import { unwrapList } from '../../utils/entityIds';
import { useTranslation } from 'react-i18next';

const ProfessionalCouncils = () => {
  const { t } = useTranslation('professional');

  const [showModal, setShowModal] = useState(false);
  const [editingCouncil, setEditingCouncil] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    sortBy: 'name',
    sortOrder: 'asc'
  });

  const { useList, useCreate, useUpdate, useDelete } = useCrud(
    'professional-councils',
    professionalService.professionalCouncilCrud
  );
  const { data: listData, isLoading, error } = useList();
  const createMutation = useCreate();
  const updateMutation = useUpdate();
  const deleteMutation = useDelete();

  const councils = unwrapList(listData).map((row) => ({
    ...row,
    status: row.is_active === false ? 'INACTIVE' : 'ACTIVE',
  }));

  const handleCreate = () => {
    setEditingCouncil(null);
    setShowModal(true);
  };

  const handleEdit = (council) => {
    setEditingCouncil(council);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir este conselho profissional?')) {
      await deleteMutation.mutateAsync(id);
    }
  };

  const handleSubmit = async (data) => {
    const payload = {
      name: data.name,
      acronym: data.acronym,
      region: data.region || 'AL',
      is_active: data.status !== 'INACTIVE',
    };
    if (editingCouncil) {
      await updateMutation.mutateAsync({ id: editingCouncil.id, data: payload });
    } else {
      await createMutation.mutateAsync({
        name: payload.name,
        acronym: payload.acronym,
        region: payload.region,
      });
    }
    setShowModal(false);
    setEditingCouncil(null);
  };

  const filteredCouncils = councils.filter((council) => {
    const matchesSearch = !filters.search ||
      String(council.name || '').toLowerCase().includes(filters.search.toLowerCase()) ||
      String(council.acronym || '').toLowerCase().includes(filters.search.toLowerCase());
    const matchesStatus = !filters.status || council.status === filters.status;
    return matchesSearch && matchesStatus;
  });

  const sortedCouncils = [...filteredCouncils].sort((a, b) => {
    const aValue = String(a[filters.sortBy] || '');
    const bValue = String(b[filters.sortBy] || '');
    if (filters.sortOrder === 'asc') {
      return aValue.localeCompare(bValue);
    }
    return bValue.localeCompare(aValue);
  });

  if (error) {
    return (
      <FormPageShell>
        <div className="alert alert-danger rounded-xl border border-red-200">
          <p className="text-red-800">{t('common:loadError')}</p>
        </div>
      </FormPageShell>
    );
  }

  return (
    <FormPageShell>
      <div className="space-y-6">
        <PageHeader
          title={t("councilsTitle")}
          description="CREA, CRM e demais conselhos de classe usados no cadastro."
          breadcrumbs={[
            { label: 'Início', to: getAppHomePath() },
            { label: 'Profissionais', to: '/professional/professional-councils' },
            { label: 'Conselhos' },
          ]}
        >
          <Can permission={P.PROFESSIONAL_ACCESS}>
            <button
              type="button"
              onClick={handleCreate}
              className="btn btn-primary inline-flex items-center gap-2 shadow-sm"
            >
              <Plus className="h-4 w-4" />
              Novo conselho
            </button>
          </Can>
        </PageHeader>

        <div className="data-list-card">
          <div className="border-b border-gray-100 bg-gradient-to-r from-slate-50/80 to-white px-5 py-4 sm:px-6">
            <ProfessionalCouncilFilters filters={filters} onFiltersChange={setFilters} />
          </div>

          <div className="p-5 sm:p-6">
            <ProfessionalCouncilTable
              councils={sortedCouncils}
              isLoading={isLoading}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onCreate={handleCreate}
            />
          </div>
        </div>
      </div>

      {showModal && (
        <ProfessionalCouncilModal
          council={editingCouncil}
          onSubmit={handleSubmit}
          onClose={() => {
            setShowModal(false);
            setEditingCouncil(null);
          }}
        />
      )}
    </FormPageShell>
  );
};

export default ProfessionalCouncils;
